<template>
  <div class="range__slider__container">
    <div class="range__slider__progress">
      <input
        type="range"
        id="input-left"
        ref="minSlider"
        :min="min"
        :max="max"
        v-model="sliderMinValue"
        :step="step"
        :style="{ zIndex: zIndexLeft }"
        :class="{ changePointerEvent: !isFirefox }"
        v-if="isMobile"
        @touchmove="changeLeft"
        @touchend="changeLeft"
      />
      <input
        type="range"
        id="input-left"
        ref="minSlider"
        :min="min"
        :max="max"
        v-model="sliderMinValue"
        :step="step"
        :style="{ zIndex: zIndexLeft }"
        :class="{ changePointerEvent: !isFirefox }"
        v-else
        @mousemove="changeLeft"
        @mouseout="changeLeft"
      />

      <input
        type="range"
        id="input-right"
        ref="maxSlider"
        :min="min"
        :max="max"
        :step="step"
        v-model="sliderMaxValue"
        :style="{ zIndex: zIndexRight }"
        :class="{ changePointerEvent: !isFirefox }"
        v-if="isMobile"
        @touchmove="changeRight"
        @touchend="changeRight"
      />
      <input
        type="range"
        id="input-right"
        ref="maxSlider"
        :min="min"
        :max="max"
        :step="step"
        v-model="sliderMaxValue"
        :style="{ zIndex: zIndexRight }"
        :class="{ changePointerEvent: !isFirefox }"
        v-else
        @mousemove="changeRight"
        @mouseout="changeRight"
      />

      <div class="slider">
        <div class="track"></div>
        <div class="range" ref="range"></div>
        <div class="range1" ref="range1"></div>
        <div class="thumb left" ref="left"></div>
        <div class="thumb right" ref="right"></div>
      </div>
    </div>

    <div aria-label="minmax heading" class="fw-ic-jb range__slider__minmax">
      <div>
        <h4 class="range__slider__minmaxtitle">Min</h4>
        <input
          class="range__slider__input"
          type="text"
          ref="minText"
          :value="formattedSliderMinValue"
          readonly
        />
      </div>
      <div>
        <h4 class="range__slider__minmaxtitle text-right">Max</h4>
        <input
          class="range__slider__input"
          type="text"
          ref="maxText"
          :value="formattedSliderMaxValue"
          readonly
        />
      </div>
    </div>

    <div aria-label="cta buttons" class="cta__btn rangeslider__ctabtn">
      <button class="cta__btntext" v-if="!hasMobile" @click="setToInitialRangeSlider">
        Clear
      </button>
      <button class="cta__btnsolid" v-if="!hasMobile" @click="calculate">
        Apply
      </button>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref, onBeforeMount, onMounted, toRefs } from "vue";
import { formatPrice } from "@/core";
import { SliderChange } from "@/constant";
import { isMobile, isTablet, isFirefox } from "mobile-device-detect";

const props = defineProps<{
  max: number;
  min: number;
  showMinRangeOnSlider: number;
  showMaxRangeOnSlider: number;
}>();

const { max, min, showMinRangeOnSlider, showMaxRangeOnSlider } = toRefs(props);

const emit = defineEmits<{
  (e: "clearPriceFilter", payload: { minShow: number; maxShow: number }): void;
  (e: "applyPriceFilter", payload: { minValue: number; maxValue: number }): void;
  (e: "updatePriceRangeMobile", payload: { minValue: number; maxValue: number }): void;
  (e: "closePriceRange"): void;
}>();

const sliderMinValue = ref(0);
const sliderMaxValue = ref(0);
const formattedSliderMinValue = ref("");
const formattedSliderMaxValue = ref("");
const step = SliderChange;
const zIndexLeft = ref(9);
const zIndexRight = ref(20);
const hasMobile = !!(isMobile && !isTablet);
const isPriceChanged = ref(false);

const minSlider = ref<HTMLInputElement | null>(null);
const maxSlider = ref<HTMLInputElement | null>(null);
const range = ref<HTMLDivElement | null>(null);
const range1 = ref<HTMLDivElement | null>(null);
const left = ref<HTMLDivElement | null>(null);
const right = ref<HTMLDivElement | null>(null);

onBeforeMount(() => {
  sliderMinValue.value = showMinRangeOnSlider.value;
  sliderMaxValue.value = showMaxRangeOnSlider.value;
});

onMounted(() => {
  changeLeft();
  changeRight();
});

const updateLeftPercentage = (percentageValue: number) => {
  if (percentageValue > 0 && percentageValue < 80 && !hasMobile) {
    percentageValue -= 4;
  } else if (percentageValue > 80 && !hasMobile) {
    percentageValue -= 8;
  } else if (percentageValue > 80 && hasMobile) {
    percentageValue -= 2;
  }
  return percentageValue;
};

const updateRightPercentage = (percentageValue: number) => {
  if (percentageValue > 0 && hasMobile) {
    percentageValue -= 4;
  } else if (percentageValue > 80 && !hasMobile) {
    percentageValue -= 4;
  }
  return percentageValue;
};

const updateZIndex = (leftIdx: number, rightIdx: number) => {
  zIndexLeft.value = leftIdx;
  zIndexRight.value = rightIdx;

  if (hasMobile) {
    emit("updatePriceRangeMobile", {
      minValue: sliderMinValue.value,
      maxValue: sliderMaxValue.value,
    });
  }
  if (isPriceChanged.value) {
    emit("applyPriceFilter", {
      minValue: sliderMinValue.value,
      maxValue: sliderMaxValue.value,
    });
  }
};

const changeLeft = () => {
  if (!minSlider.value || !maxSlider.value) return;

  minSlider.value.value = String(
    Math.min(Number(minSlider.value.value), Number(maxSlider.value.value) - step)
  );
  sliderMinValue.value = Number(minSlider.value.value);

  let percentageValue = ((sliderMinValue.value - min.value) / (max.value - min.value)) * 100;
  percentageValue = updateLeftPercentage(percentageValue);

  if (range.value) range.value.style.width = percentageValue + "%";
  if (left.value) left.value.style.left = percentageValue + "%";

  formattedSliderMinValue.value = formatPrice(sliderMinValue.value);

  if (sliderMinValue.value > min.value && !isPriceChanged.value) {
    isPriceChanged.value = true;
  }
  updateZIndex(20, 9);
};

const changeRight = () => {
  if (!minSlider.value || !maxSlider.value) return;

  maxSlider.value.value = String(
    Math.max(Number(minSlider.value.value) + step, Number(maxSlider.value.value))
  );
  sliderMaxValue.value = Number(maxSlider.value.value);

  let percentageValue =
    ((Number(maxSlider.value.max) - sliderMaxValue.value) /
      (Number(minSlider.value.max) - Number(minSlider.value.min))) *
    100;
  percentageValue = updateRightPercentage(percentageValue);

  if (range1.value) range1.value.style.width = percentageValue + "%";
  if (right.value) right.value.style.left = 100 - percentageValue + "%";

  formattedSliderMaxValue.value = formatPrice(sliderMaxValue.value);

  if (sliderMaxValue.value < max.value && !isPriceChanged.value) {
    isPriceChanged.value = true;
  }
  updateZIndex(9, 20);
};

const setToInitialRangeSlider = () => {
  sliderMinValue.value = min.value;
  sliderMaxValue.value = max.value;

  if (minSlider.value) minSlider.value.value = String(min.value);
  if (maxSlider.value) maxSlider.value.value = String(max.value);

  changeLeft();
  changeRight();
  emit("clearPriceFilter", { minShow: 0, maxShow: 0 });
  isPriceChanged.value = false;
};

const calculate = () => {
  emit("closePriceRange");
};
</script>

<style lang="scss" scoped>
.range__slider {
  &__container {
    padding: 13px 10px;
  }

  &__btn {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    column-gap: 12px;

    &text,
    &solid {
      border: none;
      outline: 0;
      cursor: pointer;
    }

    &text {
      @include font(10px, 700);
      background: transparent;
      text-transform: capitalize;
      color: $lime-green-new;
    }

    &solid {
      @include font(10px, 400);
      background: $lime-green-new;
      color: $white;
      border-radius: 4px;
      padding: 5px 10px;
      text-align: center;
    }
  }

  &__minmax {
    gap: 8px;
    margin-top: 6px;
    margin-bottom: 8px;

    > div {
      flex: 1;
    }

    &title {
      margin-bottom: 14px;
      color: $gray-600;
      font-size: 10px;

      &.text-right {
        text-align: right;
      }
    }
  }

  &__input {
    border: 1px solid #d9d9d9;
    border-radius: 4px;
    padding: 4px;
    text-align: center;
    flex: 1;
    width: 100%;
    font-size: 10px;
    pointer-events: none;
    color: $gray-600;

    &:focus {
      outline: none;
    }
  }

  &__progress {
    margin-top: 2px;

    input[type="range"] {
      position: absolute;
      -webkit-appearance: none;
      z-index: 2;
      height: 10px;
      width: 93%;
      opacity: 0;
      cursor: pointer;
      // top:0;
    }

    input[type="range"]::-webkit-slider-thumb {
      pointer-events: all;
      width: 30px;
      height: 30px;
      border-radius: 0;
      border: 0 none;
      -webkit-appearance: none;
      cursor: pointer;
    }
  }
}

.slider {
  position: relative;
  z-index: 1;
  height: 3px;

  .track {
    position: absolute;
    z-index: 1;
    inset: 0;
    border-radius: 5px;
    background-color: $lime-green-new;
  }

  .range,
  .range1 {
    position: absolute;
    z-index: 2;
    top: 0;
    bottom: 0;
    border-radius: 5px;
    background-color: lightgray;
  }

  .range1 {
    right: 0px;
  }

  .thumb {
    --size: 12px;
    position: absolute;
    z-index: 3;
    width: var(--size);
    height: var(--size);
    background-color: $white;
    border-radius: 50%;
    transition: box-shadow 0.3s ease-in-out;
    border: 2px solid $lime-green-new;

    &.left {
      left: 0%;
      transform: translate(-1px, -5px);
    }

    &.right {
      right: 0%;
      transform: translate(-12px, -5px);
      left: 100%;
    }
  }
}

.changePointerEvent {
  pointer-events: none;
}
</style>
